// src/services/airportApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

import { ProjectResponse } from '../types';

export interface Airport {
  airportId: number;
  name: string;
  timezone?: string | null;
  defaultSharePointLibrary?: string | null;
  managers?: AirportManager[];
}

export interface AirportManager {
  userId: number;
  displayName?: string; // Optional for backward compatibility
  name?: string; // Backend returns "name" field
  email: string;
  // Helper to get display name (supports both "name" and "displayName")
  getDisplayName?: () => string;
}

export interface AirportsResponse {
  airports: Airport[];
  total: number;
}

export interface AirportProjectsResponse {
  projects: ProjectResponse[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AirportApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR';
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// Get all airports (LOV - List of Values)
export async function getAirports(): Promise<AirportsResponse> {
  const response = await fetch(`${API_BASE_URL}/api/airports`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 403) {
    const errorText = await response.text();
    console.error('getAirports: 403 Forbidden - Response:', errorText);
    throw {
      error: true,
      message: 'Access denied. Airport Manager users may need a different endpoint to access airport information.',
      code: 'AUTH_REQUIRED',
    } as AirportApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('getAirports: Request failed - Status:', response.status, 'Response:', errorText);
    throw {
      error: true,
      message: `Failed to fetch airports: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as AirportApiError;
  }

  const data = await response.json();
  console.log('getAirports: Success - Response data:', data);
  return data;
}

// Get projects for a specific airport
export async function getProjectsByAirport(
  airportId: number,
  params?: {
    page?: number;
    pageSize?: number;
    status?: string;
  }
): Promise<AirportProjectsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.status) queryParams.append('status', params.status);

  const url = `${API_BASE_URL}/api/airports/${airportId}/projects${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Airport not found', code: 'NOT_FOUND' } as AirportApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to fetch projects: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as AirportApiError;
  }

  return response.json();
}

// Admin endpoints for airport CRUD operations

// Get all airports with pagination (Admin only)
export interface AdminAirportsResponse {
  airports: Airport[];
  total: number;
  page: number;
  pageSize: number;
}

export async function getAdminAirports(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
}): Promise<AdminAirportsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
  if (params?.search) queryParams.append('search', params.search);

  const url = `${API_BASE_URL}/api/admin/airports${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 404) {
    throw {
      error: true,
      message: 'Airport management endpoint not found. The backend endpoint may not be implemented yet. Please contact the backend team to implement GET /api/admin/airports',
      code: 'NOT_FOUND',
    } as AirportApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to fetch airports: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as AirportApiError;
  }

  return response.json();
}

// Create new airport (Admin only)
export interface CreateAirportRequest {
  name: string;
  timezone?: string | null;
  managers: number[]; // REQUIRED - always include this field as an array
}

export interface CreateAirportResponse {
  airport: Airport;
  success: boolean;
  message: string;
}

export async function createAirport(data: CreateAirportRequest): Promise<CreateAirportResponse> {
  const url = `${API_BASE_URL}/api/admin/airports`;
  
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as AirportApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to create airport: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as AirportApiError;
  }

  return response.json();
}

// Update airport (Admin only)
export interface UpdateAirportRequest {
  name?: string;
  timezone?: string | null;
  managers: number[]; // REQUIRED - always include this field as an array
}

export interface UpdateAirportResponse {
  airport: Airport;
  success: boolean;
  message: string;
}

export async function updateAirport(airportId: number, data: UpdateAirportRequest): Promise<UpdateAirportResponse> {
  const url = `${API_BASE_URL}/api/admin/airports/${airportId}`;
  
  // Clean up the data - remove undefined values to avoid sending unnecessary fields
  // IMPORTANT: managers field is REQUIRED and must always be an array
  const cleanedData: UpdateAirportRequest = {
    managers: Array.isArray(data.managers) ? data.managers : [], // Always include managers as array
  };
  if (data.name !== undefined && data.name !== null && data.name.trim() !== '') {
    cleanedData.name = data.name.trim();
  }
  if (data.timezone !== undefined) {
    cleanedData.timezone = data.timezone && data.timezone.trim() !== '' ? data.timezone.trim() : null;
  }
  
  console.log('updateAirport: Making request to:', url, { 
    method: 'PUT', 
    airportId, 
    cleanedData,
    originalData: data 
  });
  
  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(cleanedData),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as AirportApiError;
  }

  console.log('updateAirport: Response status:', response.status, response.statusText);

  if (response.status === 404) {
    const errorText = await response.text();
    console.error('updateAirport: 404 Not Found - URL:', url, 'Response:', errorText);
    throw { 
      error: true, 
      message: `Airport not found (ID: ${airportId}). The backend endpoint may not be implemented yet. Please verify the endpoint exists: ${url}`, 
      code: 'NOT_FOUND' 
    } as AirportApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('updateAirport: Error response:', response.status, response.statusText, errorText);
    throw {
      error: true,
      message: `Failed to update airport: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as AirportApiError;
  }

  return response.json();
}

// Delete airport (Admin only)
export interface DeleteAirportResponse {
  success: boolean;
  message: string;
}

export async function deleteAirport(airportId: number): Promise<DeleteAirportResponse> {
  const url = `${API_BASE_URL}/api/admin/airports/${airportId}`;
  
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Airport not found', code: 'NOT_FOUND' } as AirportApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to delete airport: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as AirportApiError;
  }

  // Handle 204 No Content (common for DELETE operations)
  if (response.status === 204) {
    return { success: true, message: 'Airport deleted successfully' };
  }

  // Try to parse JSON response if available
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return response.json();
  }

  // Default success response if no body
  return { success: true, message: 'Airport deleted successfully' };
}

// Assign managers to airport (bulk) - Phase 2 Sprint 27
export interface AssignManagersRequest {
  userId?: number;    // Single user (backward compatible)
  userIds?: number[]; // Multiple users (bulk)
}

export interface AssignManagersResponse {
  success: boolean;
  message: string;
  assignments: Array<{
    assignmentId: number;
    userId: number;
  }>;
  skipped: number; // Number of users already assigned
}

export async function assignManagersToAirport(
  airportId: number,
  userIds: number[]
): Promise<AssignManagersResponse> {
  const url = `${API_BASE_URL}/api/admin/airports/${airportId}/managers`;
  
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({ userIds }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Airport not found', code: 'NOT_FOUND' } as AirportApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as AirportApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to assign managers: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as AirportApiError;
  }

  return await response.json();
}

// Remove managers from airport (bulk) - Phase 2 Sprint 27
export interface RemoveManagersRequest {
  userIds: number[];
}

export interface RemoveManagersResponse {
  success: boolean;
  message: string;
  removedCount: number;
  requestedCount: number;
}

export async function removeManagersFromAirport(
  airportId: number,
  userIds: number[]
): Promise<RemoveManagersResponse> {
  const url = `${API_BASE_URL}/api/admin/airports/${airportId}/managers/bulk`;
  
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify({ userIds }),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Admin access required', code: 'AUTH_REQUIRED' } as AirportApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Airport not found', code: 'NOT_FOUND' } as AirportApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as AirportApiError;
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw {
      error: true,
      message: `Failed to remove managers: ${response.statusText}. ${errorText}`,
      code: 'NETWORK_ERROR',
    } as AirportApiError;
  }

  return await response.json();
}
